import React, { useEffect } from 'react'
import { Box, Button, GlobalStyles, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { dsColorNew } from '@palette'
import { BIActiveGoal, BIAsked, BIFormData, BIWhyOpen } from '../@signals'
import { WHY_FIELDS, fieldMarks, whyState } from '../@services/goals'
import { clearMarks, flashField, markFields, openField } from './bandFields'
import { tileSx } from '../QuerySentence'


// The marks of the fields of the band (mock `.field.auto` / `.field.choose` / `.field.flash`)
const FIELD_MARKS_CSS = {
	'[data-bi-goal="filled"] label::after': {
		content: '""', display: 'inline-block', width: 6, height: 6, borderRadius: '50%', marginLeft: 5, verticalAlign: 'middle',
		backgroundColor: dsColorNew.appBlue,
	},
	'[data-bi-goal="choose"] .MuiOutlinedInput-notchedOutline, [data-bi-goal="choose"] .rs-picker-toggle': {
		border: `1.5px dashed ${dsColorNew.warningBar} !important`,
	},
	'[data-bi-goal="choose"] label': { color: `${dsColorNew.noteAmber} !important` },
	'[data-bi-flash]': { outline: `2px solid ${dsColorNew.appBlue}`, outlineOffset: 3, borderRadius: 10, transition: 'outline-color .15s' },
}

const linkSx = { textTransform: 'none', fontSize: 13, fontWeight: 500, minWidth: 0, px: 0.75, whiteSpace: 'nowrap' }

/**
 * What the question filled and why, one line per field (CU-869fmk5ge CA 4, UX Obs. 4): ✓ filled, ○ to choose (amber),
 * dimmed with "you changed it" once the user changes it. Folds to its header after the first search (UX D2).
 */
export default function BIFilledFor({ goals }) {
	const active = BIActiveGoal.value
	const lines = active ? whyState(active.why, active.form, BIFormData.value) : []
	const marks = JSON.stringify(fieldMarks(lines))

	// the fields mount with the band: mark them after render, and clear the marks when the question goes
	useEffect(() => {
		const apply = () => markFields(JSON.parse(marks))
		apply()
		// a field still loading its options shows a skeleton without its label: mark it again once it is there
		const later = [setTimeout(apply, 800), setTimeout(apply, 2500)]
		return () => later.forEach(clearTimeout)
	}, [marks])
	useEffect(() => clearMarks, [])

	if (!active) return null

	const toChoose = lines.filter(l => l.choose && !l.chosen).length
	const searched = !!BIAsked.value
	const open = !searched || BIWhyOpen.value

	return <Box data-tour="bi-filled" sx={{ pb: 1.75, mb: 2, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
		<GlobalStyles styles={FIELD_MARKS_CSS}/>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontSize: 13, color: dsColorNew.textMuted }}>
			<Box sx={tileSx(28)}><IconSeeker icon="compass" sx={{ fontSize: 12 }}/></Box>
			<Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textMuted }}>Filled for</Typography>
			<Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>“{active.goal.question}”</Typography>
			<Box sx={{ flex: 1 }}/>
			<Typography component="span" sx={{ fontSize: 13, color: dsColorNew.textMuted, whiteSpace: 'nowrap' }}>
				{lines.length - lines.filter(l => l.choose).length} filled
				{!!toChoose && <> · <Box component="b" sx={{ color: dsColorNew.noteAmber }}>{toChoose} to choose</Box></>}
			</Typography>
			{searched && <Button variant="text" sx={linkSx} onClick={() => { BIWhyOpen.value = !BIWhyOpen.value }}>{BIWhyOpen.value ? 'Hide why' : 'Show why'}</Button>}
			<Button variant="text" sx={linkSx} onClick={() => goals.open(false)}>Other question</Button>
			<Button variant="text" sx={linkSx} onClick={goals.clear}>Clear form</Button>
		</Box>
		{open && <Box component="ul" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '4px 32px', m: '10px 0 0 38px', p: 0, listStyle: 'none' }}>
			{lines.map(line => {
				const pending = line.choose && !line.chosen
				const field = WHY_FIELDS[line.key]?.field
				return <Box component="li" key={line.key} title={`${line.label}: ${line.value} — ${line.reason}`}
					onMouseEnter={() => flashField(field, true)} onMouseLeave={() => flashField(field, false)} sx={{
					display: 'flex', gap: 1, alignItems: 'baseline', fontSize: 12.5, lineHeight: 1.45, color: dsColorNew.textMuted,
					px: 0.75, py: '2px', borderRadius: '6px', minWidth: 0, opacity: line.changed ? 0.55 : 1,
					'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
				}}>
					<Box component="span" sx={{ width: 14, flex: 'none', alignSelf: 'center', fontSize: 12, color: pending ? dsColorNew.warningBar : dsColorNew.appBlue }}>{pending ? '○' : '✓'}</Box>
					<Box component="span" sx={{ color: dsColorNew.textDark, fontWeight: 600, whiteSpace: 'nowrap' }}>{line.label}</Box>
					<Box component="span" sx={{ whiteSpace: 'nowrap', color: pending ? dsColorNew.noteAmber : dsColorNew.textTitle, fontWeight: pending ? 600 : 400 }}>
						{line.choose && line.chosen ? 'chosen' : line.value}
					</Box>
					<Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						— {line.reason}{line.changed && <i> · you changed it</i>}
					</Box>
					{pending && <Button variant="text" sx={{ ...linkSx, fontSize: 12.5, py: 0, flex: 'none' }} onClick={() => openField(field)}>Choose</Button>}
				</Box>
			})}
		</Box>}
	</Box>
}
